import type { TilePos } from '@tama/game-core';

/**
 * The world is drawn at a fixed 32 px per tile and the whole map is then scaled
 * to fit whatever screen it lands on, so every number in the scene layer is a
 * stable design-time value rather than something that shifts with the viewport.
 *
 * Tile (0, 0) is the top-left of the map; y grows downwards, matching the grid
 * coordinates in `@tama/game-core` exactly. Nothing multiplies by TILE outside
 * this module and the drawing code it serves.
 */
export const TILE = 32;

/** Design-space size of a map, in pixels. */
export function worldPixelSize(world: { width: number; height: number }) {
  return { width: world.width * TILE, height: world.height * TILE };
}

/** Top-left pixel corner of a tile. */
export function tileOrigin(x: number, y: number) {
  return { x: x * TILE, y: y * TILE };
}

/** Centre of a tile — where a 1x1 object is drawn from. */
export function tileCenter(x: number, y: number) {
  return { x: x * TILE + TILE / 2, y: y * TILE + TILE / 2 };
}

/**
 * Where a character standing on (x, y) puts its feet.
 *
 * Slightly above the bottom edge rather than on it: the sprite is drawn from
 * the feet up, and planting it flush with the tile boundary makes it look like
 * it is standing in the tile behind.
 */
export function standingPoint(x: number, y: number) {
  return { x: x * TILE + TILE / 2, y: y * TILE + TILE * 0.86 };
}

/** Which tile a design-space point falls in. Used by dev mode's pointer handling. */
export function pointToTile(px: number, py: number): TilePos {
  return { x: Math.floor(px / TILE), y: Math.floor(py / TILE) };
}

/**
 * The depth key a drawable sorts by.
 *
 * Everything is sorted by the y of its *base*, so a pet standing below a table
 * is drawn in front of it and one standing above is drawn behind — the whole of
 * the illusion of depth in a top-down map, in one number.
 */
export function depthOf(baseY: number): number {
  return Math.round(baseY * 100);
}
